import { useMemo } from "react";
import {
  Button,
  Chip,
  Division,
  Flex,
  Icon,
  Icons,
  Paragraph,
  Table,
  type TableColumnType,
} from "@ir/ui";
import {
  MasterDataPurchaser,
  type MasterDataPurchaser as MasterDataPurchaserEntity,
} from "@src/domain/entities/masterData";
import { Permissions } from "@src/domain/permissions";
import HasAnyPermission from "@src/presentation/components/permissions/HasAnyPermission";
import { useAutoHeight } from "@src/presentation/hooks/autoheight";
import {
  formatMasterDataCellText,
  MASTER_DATA_COLUMN_VERTICAL_ALIGN,
  MasterDataTableEmptyState,
  sumMasterDataColumnWidths,
} from "./masterDataTable.shared";

export interface MasterDataPurchaserDcTarget {
  purchaserId: number;
  purchaserName: string;
  distributionCentersCount: number;
}

type PurchaserFieldKey =
  | "purchaserName"
  | "purchaserKey"
  | "isActive"
  | "searchKeywords"
  | "defaultProductCategoryName"
  | "metadata"
  | "intelligenceFilter"
  | "discountPercentageCredit"
  | "deductionTypeInvoicePatternForDiscount"
  | "discountPercentageDeduction"
  | "discountTermAgreementDays"
  | "additionalFieldMapping"
  | "discountTermAgreementDaysTolerance";

type AttributeRowKind = "text" | "status" | "json" | "dc";

type AttributeRowDef =
  | {
      key: PurchaserFieldKey;
      label: string;
      kind: "text" | "status" | "json";
    }
  | { key: "dc"; label: string; kind: "dc" };

/** One matrix row = one spreadsheet attribute; purchaser values are keyed by id. */
interface PurchaserMatrixRow {
  key: string;
  attributeLabel: string;
  kind: AttributeRowKind;
  valuesByPurchaserId: Record<string, unknown>;
}

const ATTRIBUTE_ROWS: AttributeRowDef[] = [
  { key: "purchaserKey", label: "Purchaser Key", kind: "text" },
  { key: "isActive", label: "Status", kind: "status" },
  { key: "searchKeywords", label: "Search Keywords", kind: "text" },
  {
    key: "defaultProductCategoryName",
    label: "Default Product Category",
    kind: "text",
  },
  { key: "metadata", label: "Metadata", kind: "json" },
  { key: "intelligenceFilter", label: "Intelligence Filter", kind: "json" },
  {
    key: "discountPercentageCredit",
    label: "Credit Term Discount %",
    kind: "text",
  },
  {
    key: "deductionTypeInvoicePatternForDiscount",
    label: "Deduction Type Invoice Pattern For Term Discount",
    kind: "json",
  },
  {
    key: "discountPercentageDeduction",
    label: "Deduction Term Discount %",
    kind: "text",
  },
  {
    key: "discountTermAgreementDays",
    label: "Term Discount Agreement Days",
    kind: "text",
  },
  {
    key: "additionalFieldMapping",
    label: "Additional Field Mapping",
    kind: "json",
  },
  {
    key: "discountTermAgreementDaysTolerance",
    label: "Term Agreement Days Tolerance",
    kind: "text",
  },
  { key: "dc", label: "Distribution Centers", kind: "dc" },
];

const PARAMETER_COLUMN_WIDTH = 280;
const PURCHASER_COLUMN_WIDTH = 220;

function isEmptyValue(value: unknown): boolean {
  return value == null || value === "";
}

function renderJsonEye(
  title: string,
  value: unknown,
  onViewJson: (title: string, value: unknown) => void,
) {
  if (isEmptyValue(value)) {
    return <Paragraph className="m-0 text-grey-500">—</Paragraph>;
  }
  return (
    <Flex align="center" justifyContent="start">
      <Button
        size="small"
        aria-label={`View ${title}`}
        onPress={() => onViewJson(title, value)}
        style={{
          padding: "2px 6px",
          fontSize: "12px",
          letterSpacing: "1.25px",
        }}
      >
        View
      </Button>
    </Flex>
  );
}

function renderDcEye(
  value: unknown,
  onViewDc: (purchaser: MasterDataPurchaserDcTarget) => void,
) {
  const target = value as MasterDataPurchaserDcTarget | undefined;
  if (target?.purchaserId == null) {
    return <Paragraph className="m-0 text-grey-500">—</Paragraph>;
  }
  const label = target.purchaserName || String(target.purchaserId);
  const count = target.distributionCentersCount ?? 0;
  const viewLabel = `View(${count})`;
  return (
    <Flex align="center" justifyContent="start">
      <Button
        size="small"
        aria-label={`${viewLabel} distribution centers for ${label}`}
        onPress={() => onViewDc(target)}
        style={{
          padding: "2px 6px",
          fontSize: "12px",
          letterSpacing: "1.25px",
        }}
      >
        {viewLabel}
      </Button>
    </Flex>
  );
}

function renderPurchaserColumnTitle(
  entity: MasterDataPurchaserEntity,
  onEdit: (purchaser: MasterDataPurchaserEntity) => void,
) {
  const title =
    entity.purchaserName?.trim() ||
    entity.purchaserKey ||
    String(entity.purchaserId);

  return (
    <Flex align="center" justifyContent="start" wrap="no-wrap" gap={0.5}>
      <span>{title}</span>
      <HasAnyPermission requiredPermissions={[Permissions.MASTER_DATA_EDIT]}>
        <Button
          size="small"
          variant="outline"
          color="grey"
          aria-label={`Edit ${title}`}
          onPress={() => onEdit(entity)}
        >
          <Icon name={Icons.Edit} size="small" />
        </Button>
      </HasAnyPermission>
    </Flex>
  );
}

function renderPurchaserValueCell(
  kind: AttributeRowKind,
  label: string,
  value: unknown,
  onViewJson: (title: string, value: unknown) => void,
  onViewDc: (purchaser: MasterDataPurchaserDcTarget) => void,
) {
  if (kind === "status") {
    return (
      <Flex align="center" justifyContent="start">
        <Chip
          label={value ? "Active" : "Inactive"}
          color={value ? "success" : "error"}
          size="small"
        />
      </Flex>
    );
  }
  if (kind === "json") {
    return renderJsonEye(label, value, onViewJson);
  }
  if (kind === "dc") {
    return renderDcEye(value, onViewDc);
  }
  return (
    <Paragraph className="m-0">{formatMasterDataCellText(value)}</Paragraph>
  );
}

function mapPurchasersToMatrixRows(
  purchasers: MasterDataPurchaserEntity[],
): PurchaserMatrixRow[] {
  const purchaserJson = purchasers.map((entity) =>
    MasterDataPurchaser.toJSON(entity),
  );

  return ATTRIBUTE_ROWS.map((row) => {
    const valuesByPurchaserId: Record<string, unknown> = {};
    for (let index = 0; index < purchasers.length; index += 1) {
      const purchaser = purchaserJson[index];
      const purchaserId = String(purchaser.purchaserId);
      if (row.kind === "dc") {
        valuesByPurchaserId[purchaserId] = {
          purchaserId: purchaser.purchaserId,
          purchaserName:
            purchaser.purchaserName?.trim() ||
            purchaser.purchaserKey ||
            purchaserId,
          distributionCentersCount: purchaser.distributionCentersCount ?? 0,
        } satisfies MasterDataPurchaserDcTarget;
      } else {
        valuesByPurchaserId[purchaserId] = purchaser[row.key];
      }
    }
    return {
      key: row.key,
      attributeLabel: row.label,
      kind: row.kind,
      valuesByPurchaserId,
    };
  });
}

function buildColumns(
  purchasers: MasterDataPurchaserEntity[],
  onViewJson: (title: string, value: unknown) => void,
  onViewDc: (purchaser: MasterDataPurchaserDcTarget) => void,
  onEdit: (purchaser: MasterDataPurchaserEntity) => void,
): TableColumnType<PurchaserMatrixRow>[] {
  const parameterColumn: TableColumnType<PurchaserMatrixRow> = {
    title: "Purchaser Name",
    dataIndex: "attributeLabel",
    key: "attributeLabel",
    align: "left",
    verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
    width: PARAMETER_COLUMN_WIDTH,
    fixed: "left",
    cellRender: (value: unknown) => (
      <Paragraph className="m-0 text-grey-700 font-bold">
        {formatMasterDataCellText(value)}
      </Paragraph>
    ),
  };

  const purchaserColumns: TableColumnType<PurchaserMatrixRow>[] =
    purchasers.map((entity) => {
      const purchaserId = String(entity.purchaserId);

      return {
        title: renderPurchaserColumnTitle(entity, onEdit),
        dataIndex: "attributeLabel",
        key: `purchaser-${purchaserId}`,
        align: "left",
        verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
        width: PURCHASER_COLUMN_WIDTH,
        cellRender: (_value: unknown, record: PurchaserMatrixRow) =>
          renderPurchaserValueCell(
            record.kind,
            record.attributeLabel,
            record.valuesByPurchaserId[purchaserId],
            onViewJson,
            onViewDc,
          ),
      };
    });

  return [parameterColumn, ...purchaserColumns];
}

interface MasterDataPurchasersMatrixTableProps {
  purchasers: MasterDataPurchaserEntity[];
  onViewJson: (title: string, value: unknown) => void;
  onViewDc: (purchaser: MasterDataPurchaserDcTarget) => void;
  onEdit: (purchaser: MasterDataPurchaserEntity) => void;
}

export const MasterDataPurchasersMatrixTable = ({
  purchasers,
  onViewJson,
  onViewDc,
  onEdit,
}: MasterDataPurchasersMatrixTableProps) => {
  const { ref: tableScrollContainerRef, height: tableHeight } =
    useAutoHeight<HTMLDivElement>();

  const columns = useMemo(
    () => buildColumns(purchasers, onViewJson, onViewDc, onEdit),
    [purchasers, onViewJson, onViewDc, onEdit],
  );
  const dataSource = useMemo(
    () => mapPurchasersToMatrixRows(purchasers),
    [purchasers],
  );
  const tableWidth = useMemo(
    () => sumMasterDataColumnWidths(columns),
    [columns],
  );

  return (
    <Division
      ref={tableScrollContainerRef}
      className="master-data-purchasers-matrix"
      style={{ flex: 1, minHeight: 0, minWidth: 0 }}
    >
      {purchasers.length === 0 ? (
        <MasterDataTableEmptyState
          search=""
          entityLabel="purchasers"
          onClearSearch={() => undefined}
        />
      ) : (
        <Table<PurchaserMatrixRow>
          className="master-data-purchasers-matrix-table"
          columns={columns}
          dataSource={dataSource}
          border="row"
          rowHoverable
          aria-label="Purchasers matrix table"
          sticky
          scroll={{ x: tableWidth, y: tableHeight }}
        />
      )}
    </Division>
  );
};
